import React, { useEffect, useState } from "react";
import { Link, useNavigate, Routes, Route, NavLink } from "react-router-dom";
import axios from "axios";
import { useApp, API } from "../store";
import { LayoutDashboard, Package, ShoppingCart, Users, MapPin, Mail, LogOut, Plus, Trash2, Edit, Coins, FileText, Globe, Image as ImageIcon, BarChart3, Calendar, Share2 } from "lucide-react";
import { ContentManager, BlogManager } from "./AdminExtra";
import { LessonReservations, AdvancedMetrics, MediaLibrary, EmbedsAndSettings, ProductEditorPro, LessonSessionsManager, AddonsManager } from "./AdminAdvanced";

const fmt = (n) => (Number(n) || 0).toLocaleString("pt-BR", { style: "currency", currency: "USD", maximumFractionDigits: 0 });

export default function Admin() {
  const { token, logout } = useApp();
  const navigate = useNavigate();
  useEffect(() => { if (!token) navigate("/admin/login"); }, [token, navigate]);
  if (!token) return null;

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 flex" data-testid="admin-app">
      <aside className="w-60 border-r border-slate-200 bg-white p-5 flex flex-col gap-1 font-ui text-sm sticky top-0 h-screen">
        <div className="mb-8">
          <div className="font-ui font-black tracking-tighter text-lg text-slate-900">SharkX Admin</div>
          <div className="overline text-[10px]">Painel</div>
        </div>
        <NavItem to="/admin" exact icon={<LayoutDashboard size={16} />} label="Visão Geral" />
        <NavItem to="/admin/metrics" icon={<BarChart3 size={16} />} label="Métricas" />
        <NavItem to="/admin/products" icon={<Package size={16} />} label="Produtos" />
        <NavItem to="/admin/blog" icon={<FileText size={16} />} label="Blog" />
        <NavItem to="/admin/content" icon={<Globe size={16} />} label="Conteúdo" />
        <NavItem to="/admin/media" icon={<ImageIcon size={16} />} label="Mídia" />
        <NavItem to="/admin/orders" icon={<ShoppingCart size={16} />} label="Pedidos" />
        <NavItem to="/admin/lessons" icon={<Calendar size={16} />} label="Reservas" />
        <NavItem to="/admin/sessions" icon={<Calendar size={16} />} label="Sessões/Diárias" />
        <NavItem to="/admin/addons" icon={<Package size={16} />} label="Extras" />
        <NavItem to="/admin/customers" icon={<Users size={16} />} label="Clientes" />
        <NavItem to="/admin/locations" icon={<MapPin size={16} />} label="Locais" />
        <NavItem to="/admin/payments" icon={<Coins size={16} />} label="Pagamentos" />
        <NavItem to="/admin/leads" icon={<Mail size={16} />} label="Leads" />
        <NavItem to="/admin/embeds" icon={<Share2 size={16} />} label="Embeds & Config" />
        <button onClick={() => { logout(); navigate("/admin/login"); }} className="mt-auto flex items-center gap-2 px-3 py-2 hover:bg-slate-100 text-slate-600 hover:text-slate-900 rounded" data-testid="admin-logout"><LogOut size={16} /> Sair</button>
        <Link to="/" className="flex items-center gap-2 px-3 py-2 hover:bg-slate-100 text-slate-500 text-xs rounded">← Voltar ao site</Link>
      </aside>
      <main className="flex-1 p-10">
        <Routes>
          <Route index element={<Overview />} />
          <Route path="metrics" element={<AdvancedMetrics />} />
          <Route path="products" element={<Products />} />
          <Route path="blog" element={<BlogManager />} />
          <Route path="content" element={<ContentManager />} />
          <Route path="media" element={<MediaLibrary />} />
          <Route path="orders" element={<Orders />} />
          <Route path="lessons" element={<LessonReservations />} />
          <Route path="sessions" element={<LessonSessionsManager />} />
          <Route path="addons" element={<AddonsManager />} />
          <Route path="customers" element={<Customers />} />
          <Route path="locations" element={<Locations />} />
          <Route path="payments" element={<Payments />} />
          <Route path="leads" element={<Leads />} />
          <Route path="embeds" element={<EmbedsAndSettings />} />
        </Routes>
      </main>
    </div>
  );
}

function NavItem({ to, icon, label, exact }) {
  return (
    <NavLink to={to} end={exact} className={({ isActive }) => `flex items-center gap-2 px-3 py-2 rounded ${isActive ? "bg-sky-500 text-white" : "text-slate-700 hover:bg-slate-100"}`} data-testid={`nav-${label.toLowerCase()}`}>
      {icon} <span>{label}</span>
    </NavLink>
  );
}

function Overview() {
  const { authHeaders } = useApp();
  const [stats, setStats] = useState(null);
  useEffect(() => { axios.get(`${API}/admin/stats`, authHeaders()).then((r) => setStats(r.data)); }, []);
  if (!stats) return <div>Carregando…</div>;
  return (
    <div data-testid="admin-overview">
      <div className="overline mb-2">Painel</div>
      <h1 className="font-serif-display text-5xl text-slate-900">Visão Geral</h1>
      <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-4">
        <Stat label="Receita" value={fmt(stats.revenue)} />
        <Stat label="Pedidos" value={stats.orders} />
        <Stat label="Clientes" value={stats.customers} />
        <Stat label="Produtos" value={stats.products} />
        <Stat label="Reservas" value={stats.bookings} />
        <Stat label="Leads Ultra" value={stats.ultra_leads} />
        <Stat label="Newsletter" value={stats.newsletter} />
      </div>
      <div className="mt-10 bg-white border border-slate-200 rounded-md p-6">
        <div className="overline mb-4">Receita diária</div>
        <div className="flex items-end gap-1 h-48">
          {stats.daily.length === 0 ? <div className="text-slate-400">Sem pedidos ainda.</div> :
            stats.daily.map((d) => {
              const max = Math.max(...stats.daily.map((x) => x.revenue));
              const h = max ? (d.revenue / max) * 100 : 0;
              return <div key={d.date} className="flex-1 bg-sky-400 hover:bg-sky-500 rounded-t" style={{ height: `${h}%` }} title={`${d.date}: ${fmt(d.revenue)}`} />;
            })}
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="bg-white border border-slate-200 p-5 rounded-md" data-testid={`stat-${label.toLowerCase().replace(/ /g, "-")}`}>
      <div className="overline">{label}</div>
      <div className="font-serif-display text-4xl mt-2 text-sky-600">{value}</div>
    </div>
  );
}

function Products() {
  const { authHeaders } = useApp();
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = () => axios.get(`${API}/products`).then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const del = async (id) => {
    if (!confirm("Excluir?")) return;
    await axios.delete(`${API}/products/${id}`, authHeaders());
    load();
  };

  return (
    <div data-testid="admin-products">
      <div className="flex items-center justify-between">
        <div>
          <div className="overline">Catálogo</div>
          <h1 className="font-serif-display text-5xl text-slate-900">Produtos</h1>
          <div className="text-xs text-slate-500 mt-2">As alterações aparecem em todas as línguas (PT/EN/ES).</div>
        </div>
        <button onClick={() => setEditing({})} className="btn-primary" data-testid="add-product"><Plus size={14} /> Novo</button>
      </div>
      <div className="mt-10 bg-white border border-slate-200 rounded-md overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-100 text-slate-600">
            <tr><th className="text-left p-3">Nome</th><th className="text-left p-3">Categoria</th><th className="text-left p-3">Preço</th><th className="text-left p-3">Carbon+</th><th className="text-left p-3">Estoque</th><th></th></tr>
          </thead>
          <tbody>
            {items.map((p) => (
              <tr key={p.id} className="border-t border-slate-200">
                <td className="p-3 font-semibold">{p.name}</td>
                <td className="p-3 text-slate-500">{p.category}</td>
                <td className="p-3">{fmt(p.base_price)}</td>
                <td className="p-3">{fmt(p.carbon_upcharge)}</td>
                <td className="p-3">{p.stock}</td>
                <td className="p-3 text-right">
                  <button onClick={() => setEditing(p)} className="text-slate-600 hover:text-slate-900 p-1" data-testid={`edit-${p.slug}`}><Edit size={14} /></button>
                  <button onClick={() => del(p.id)} className="text-red-500 hover:text-red-600 p-1 ml-2" data-testid={`del-${p.slug}`}><Trash2 size={14} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {editing && <ProductEditorPro product={editing} onClose={() => setEditing(null)} onSave={load} />}
    </div>
  );
}

function ProductEditor({ product, onClose, onSave }) {
  const { authHeaders } = useApp();
  const [f, setF] = useState({
    slug: product.slug || "", name: product.name || "", category: product.category || "efoils",
    tagline: product.tagline || "", description: product.description || "",
    base_price: product.base_price || 0,
    price_brl_override: product.price_brl_override ?? "",
    carbon_upcharge: product.carbon_upcharge || 0,
    colors: (product.colors || []).join(","), sizes: (product.sizes || []).join(","),
    wing_options: (product.wing_options || []).join(","), stabilizer_options: (product.stabilizer_options || []).join(","),
    images: (product.images || []).join(","),
    battery_options: JSON.stringify(product.battery_options || []),
    specs: JSON.stringify(product.specs || {}),
    featured: !!product.featured, is_accessory: !!product.is_accessory, stock: product.stock ?? 100,
  });

  const save = async () => {
    const payload = {
      slug: f.slug, name: f.name, category: f.category, tagline: f.tagline, description: f.description,
      base_price: Number(f.base_price),
      price_brl_override: f.price_brl_override !== "" && f.price_brl_override != null ? Number(f.price_brl_override) : null,
      carbon_upcharge: Number(f.carbon_upcharge),
      colors: f.colors.split(",").map((s) => s.trim()).filter(Boolean),
      sizes: f.sizes.split(",").map((s) => s.trim()).filter(Boolean),
      wing_options: f.wing_options.split(",").map((s) => s.trim()).filter(Boolean),
      stabilizer_options: f.stabilizer_options.split(",").map((s) => s.trim()).filter(Boolean),
      images: f.images.split(",").map((s) => s.trim()).filter(Boolean),
      battery_options: JSON.parse(f.battery_options || "[]"),
      specs: JSON.parse(f.specs || "{}"),
      featured: f.featured, is_accessory: f.is_accessory, stock: Number(f.stock),
    };
    if (product.id) await axios.put(`${API}/products/${product.id}`, payload, authHeaders());
    else await axios.post(`${API}/products`, payload, authHeaders());
    onSave(); onClose();
  };

  return (
    <div className="fixed inset-0 bg-slate-900/50 z-50 grid place-items-center p-6 overflow-y-auto" data-testid="product-editor">
      <div className="bg-white border border-slate-200 max-w-3xl w-full p-8 max-h-[90vh] overflow-y-auto rounded-md">
        <h3 className="font-serif-display text-3xl mb-6 text-slate-900">{product.id ? "Editar" : "Novo"} produto</h3>
        <div className="grid grid-cols-2 gap-3 font-ui text-sm">
          <Field label="Slug" v={f.slug} set={(v) => setF({ ...f, slug: v })} />
          <Field label="Nome" v={f.name} set={(v) => setF({ ...f, name: v })} />
          <Field label="Categoria" v={f.category} set={(v) => setF({ ...f, category: v })} />
          <Field label="Tagline" v={f.tagline} set={(v) => setF({ ...f, tagline: v })} />
          <Field label="Preço base (USD)" type="number" v={f.base_price} set={(v) => setF({ ...f, base_price: v })} />
          <Field label="Preço BRL fixo (opcional)" type="number" v={f.price_brl_override ?? ""} set={(v) => setF({ ...f, price_brl_override: v })} />
          <Field label="Upgrade carbon" type="number" v={f.carbon_upcharge} set={(v) => setF({ ...f, carbon_upcharge: v })} />
          <Field label="Cores (csv)" v={f.colors} set={(v) => setF({ ...f, colors: v })} />
          <Field label="Tamanhos (csv)" v={f.sizes} set={(v) => setF({ ...f, sizes: v })} />
          <Field label="Asas (csv)" v={f.wing_options} set={(v) => setF({ ...f, wing_options: v })} />
          <Field label="Estabilizadores (csv)" v={f.stabilizer_options} set={(v) => setF({ ...f, stabilizer_options: v })} />
          <Field label="Imagens (csv URLs)" v={f.images} set={(v) => setF({ ...f, images: v })} full />
          <Field label="Baterias (JSON)" v={f.battery_options} set={(v) => setF({ ...f, battery_options: v })} full />
          <Field label="Specs (JSON)" v={f.specs} set={(v) => setF({ ...f, specs: v })} full />
          <Field label="Estoque" type="number" v={f.stock} set={(v) => setF({ ...f, stock: v })} />
          <label className="flex items-center gap-2 mt-3"><input type="checkbox" checked={f.featured} onChange={(e) => setF({ ...f, featured: e.target.checked })} /> Destaque</label>
          <label className="flex items-center gap-2 mt-3"><input type="checkbox" checked={f.is_accessory} onChange={(e) => setF({ ...f, is_accessory: e.target.checked })} /> Acessório</label>
          <textarea placeholder="Descrição" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} rows={4} className="col-span-2 bg-white border border-slate-300 px-3 py-2 rounded"></textarea>
        </div>
        <div className="mt-6 flex gap-3 justify-end">
          <button onClick={onClose} className="btn-ghost" data-testid="editor-cancel">Cancelar</button>
          <button onClick={save} className="btn-primary" data-testid="editor-save">Salvar</button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, v, set, type = "text", full }) {
  return (
    <label className={full ? "col-span-2" : ""}>
      <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1 font-ui">{label}</div>
      <input type={type} value={v} onChange={(e) => set(e.target.value)} className="w-full bg-white border border-slate-300 px-3 py-2 rounded focus:border-sky-500 focus:outline-none" />
    </label>
  );
}

function Orders() {
  const { authHeaders } = useApp();
  const [orders, setOrders] = useState([]);
  useEffect(() => { axios.get(`${API}/admin/orders`, authHeaders()).then((r) => setOrders(r.data)); }, []);
  return (
    <div data-testid="admin-orders">
      <div className="overline">Vendas</div>
      <h1 className="font-serif-display text-5xl text-slate-900">Pedidos</h1>
      <div className="mt-10 bg-white border border-slate-200 rounded-md overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-100 text-slate-600"><tr><th className="text-left p-3">Data</th><th className="text-left p-3">Cliente</th><th className="text-left p-3">Método</th><th className="text-left p-3">Valor</th><th className="text-left p-3">Itens</th></tr></thead>
          <tbody>
            {orders.length === 0 && <tr><td colSpan={5} className="p-6 text-center text-slate-400">Sem pedidos ainda.</td></tr>}
            {orders.map((o) => (
              <tr key={o.id} className="border-t border-slate-200">
                <td className="p-3 text-slate-500">{o.created_at?.slice(0, 16).replace("T", " ")}</td>
                <td className="p-3">{o.customer_name || "—"}<br /><span className="text-slate-500 text-xs">{o.customer_email}</span></td>
                <td className="p-3"><span className="spec-pill">{o.method || "stripe"}</span></td>
                <td className="p-3 text-sky-600 font-semibold">{fmt(o.amount)}</td>
                <td className="p-3 text-slate-500 text-xs">{o.metadata?.items}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Customers() {
  const { authHeaders } = useApp();
  const [rows, setRows] = useState([]);
  useEffect(() => { axios.get(`${API}/admin/customers`, authHeaders()).then((r) => setRows(r.data)); }, []);
  return (
    <div data-testid="admin-customers">
      <div className="overline">CRM</div>
      <h1 className="font-serif-display text-5xl text-slate-900">Clientes</h1>
      <div className="mt-10 bg-white border border-slate-200 rounded-md overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-100 text-slate-600"><tr><th className="text-left p-3">Nome</th><th className="text-left p-3">Email</th><th className="text-left p-3">Pedidos</th><th className="text-left p-3">Total</th></tr></thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={4} className="p-6 text-center text-slate-400">Sem clientes ainda.</td></tr>}
            {rows.map((c) => (
              <tr key={c.id} className="border-t border-slate-200">
                <td className="p-3 font-semibold">{c.name || "—"}</td>
                <td className="p-3 text-slate-600">{c.email}</td>
                <td className="p-3">{c.total_orders}</td>
                <td className="p-3 text-sky-600 font-semibold">{fmt(c.total_spent)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Locations() {
  const { authHeaders } = useApp();
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = () => axios.get(`${API}/admin/locations`, authHeaders()).then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);

  const del = async (id) => { if (!confirm("Excluir?")) return; await axios.delete(`${API}/locations/${id}`, authHeaders()); load(); };

  return (
    <div data-testid="admin-locations">
      <div className="flex items-center justify-between">
        <div><div className="overline">Centros parceiros</div><h1 className="font-serif-display text-5xl text-slate-900">Locais</h1></div>
        <button onClick={() => setEditing({ name: "", city: "", country: "", lat: 0, lng: 0, services: ["demo"], active: true })} className="btn-primary" data-testid="add-location"><Plus size={14} /> Novo</button>
      </div>
      <div className="mt-10 bg-white border border-slate-200 rounded-md overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-100 text-slate-600"><tr><th className="text-left p-3">Nome</th><th className="text-left p-3">Cidade</th><th className="text-left p-3">País</th><th className="text-left p-3">Serviços</th><th></th></tr></thead>
          <tbody>
            {rows.map((l) => (
              <tr key={l.id} className="border-t border-slate-200">
                <td className="p-3 font-semibold">{l.name}</td><td className="p-3 text-slate-600">{l.city}</td><td className="p-3 text-slate-600">{l.country}</td>
                <td className="p-3 text-xs text-slate-500">{(l.services || []).join(", ")}</td>
                <td className="p-3 text-right">
                  <button onClick={() => setEditing(l)} className="text-slate-600 p-1"><Edit size={14} /></button>
                  <button onClick={() => del(l.id)} className="text-red-500 p-1 ml-2"><Trash2 size={14} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {editing && <LocationEditor loc={editing} onClose={() => setEditing(null)} onSave={load} />}
    </div>
  );
}

function LocationEditor({ loc, onClose, onSave }) {
  const { authHeaders } = useApp();
  const [f, setF] = useState({ ...loc, services: (loc.services || []).join(",") });
  const save = async () => {
    const payload = { ...f, lat: Number(f.lat), lng: Number(f.lng), services: f.services.split(",").map((s) => s.trim()).filter(Boolean) };
    delete payload.id;
    if (loc.id) await axios.put(`${API}/locations/${loc.id}`, payload, authHeaders());
    else await axios.post(`${API}/locations`, payload, authHeaders());
    onSave(); onClose();
  };
  return (
    <div className="fixed inset-0 bg-slate-900/50 z-50 grid place-items-center p-6" data-testid="location-editor">
      <div className="bg-white border border-slate-200 max-w-xl w-full p-8 rounded-md">
        <h3 className="font-serif-display text-3xl mb-6 text-slate-900">{loc.id ? "Editar" : "Novo"} local</h3>
        <div className="grid grid-cols-2 gap-3 font-ui text-sm">
          <Field label="Nome" v={f.name} set={(v) => setF({ ...f, name: v })} />
          <Field label="Cidade" v={f.city} set={(v) => setF({ ...f, city: v })} />
          <Field label="País" v={f.country} set={(v) => setF({ ...f, country: v })} />
          <Field label="Endereço" v={f.address || ""} set={(v) => setF({ ...f, address: v })} />
          <Field label="Lat" v={f.lat} set={(v) => setF({ ...f, lat: v })} />
          <Field label="Lng" v={f.lng} set={(v) => setF({ ...f, lng: v })} />
          <Field label="Telefone" v={f.phone || ""} set={(v) => setF({ ...f, phone: v })} />
          <Field label="Email" v={f.email || ""} set={(v) => setF({ ...f, email: v })} />
          <Field label="Serviços (csv)" v={f.services} set={(v) => setF({ ...f, services: v })} full />
          <label className="flex items-center gap-2 mt-2 col-span-2"><input type="checkbox" checked={!!f.active} onChange={(e) => setF({ ...f, active: e.target.checked })} /> Ativo</label>
        </div>
        <div className="mt-6 flex gap-3 justify-end">
          <button onClick={onClose} className="btn-ghost">Cancelar</button>
          <button onClick={save} className="btn-primary">Salvar</button>
        </div>
      </div>
    </div>
  );
}

function Payments() {
  const { authHeaders } = useApp();
  const [txs, setTxs] = useState([]);
  useEffect(() => { axios.get(`${API}/admin/transactions`, authHeaders()).then((r) => setTxs(r.data)).catch(() => {}); }, []);
  return (
    <div data-testid="admin-payments">
      <div className="overline">Transações</div>
      <h1 className="font-serif-display text-5xl text-slate-900">Pagamentos</h1>
      <div className="text-xs text-slate-500 mt-2">PIX (InfinitePay) e USDT TRC20 (TronGrid)</div>
      <div className="mt-10 bg-white border border-slate-200 rounded-md overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-100 text-slate-600"><tr><th className="text-left p-3">Data</th><th className="text-left p-3">Método</th><th className="text-left p-3">Status</th><th className="text-left p-3">Valor</th><th className="text-left p-3">TX/Ref</th><th className="text-left p-3">Order</th></tr></thead>
          <tbody>
            {txs.length === 0 && <tr><td colSpan={6} className="p-6 text-center text-slate-400">Sem transações alternativas ainda.</td></tr>}
            {txs.map((t) => (
              <tr key={t.id} className="border-t border-slate-200">
                <td className="p-3 text-slate-500">{t.created_at?.slice(0, 16).replace("T", " ")}</td>
                <td className="p-3"><span className="spec-pill">{t.method?.replace("_", " ")}</span></td>
                <td className="p-3"><PayStatus s={t.payment_status} /></td>
                <td className="p-3">{t.method === "pix_infinitepay" ? `R$ ${(t.amount_brl_cents/100).toFixed(2)}` : `${t.expected_usdt} USDT`}</td>
                <td className="p-3 text-xs text-slate-500 font-mono break-all max-w-[260px]">{t.tx_hash || "—"}</td>
                <td className="p-3 text-xs text-slate-500 font-mono">{t.order_id?.slice(0, 8)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function PayStatus({ s }) {
  const map = {
    paid: "bg-emerald-100 text-emerald-700",
    pending: "bg-amber-100 text-amber-700",
    expired: "bg-slate-100 text-slate-600",
    underpaid: "bg-orange-100 text-orange-700",
    failed: "bg-red-100 text-red-700",
  };
  return <span className={`text-[10px] uppercase tracking-widest px-2 py-1 rounded ${map[s] || "bg-slate-100 text-slate-600"}`}>{s}</span>;
}

function Leads() {
  const { authHeaders } = useApp();
  const [news, setNews] = useState([]); const [ultra, setUltra] = useState([]); const [bookings, setBookings] = useState([]); const [contacts, setContacts] = useState([]);
  useEffect(() => {
    axios.get(`${API}/admin/newsletter`, authHeaders()).then((r) => setNews(r.data));
    axios.get(`${API}/admin/ultra-leads`, authHeaders()).then((r) => setUltra(r.data));
    axios.get(`${API}/admin/bookings`, authHeaders()).then((r) => setBookings(r.data));
    axios.get(`${API}/admin/contacts`, authHeaders()).then((r) => setContacts(r.data));
  }, []);
  return (
    <div data-testid="admin-leads">
      <div className="overline">Pipeline</div>
      <h1 className="font-serif-display text-5xl text-slate-900">Leads</h1>
      <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-6">
        <LeadBox title="Lista Ultra AI" items={ultra.map((u) => `${u.name || ""} ${u.email}`)} />
        <LeadBox title="Newsletter" items={news.map((n) => n.email)} />
        <LeadBox title="Reservas" items={bookings.map((b) => `${b.name} · ${b.type} · ${b.date}`)} />
        <LeadBox title="Contatos" items={contacts.map((c) => `${c.name} · ${c.email}`)} />
      </div>
    </div>
  );
}

function LeadBox({ title, items }) {
  return (
    <div className="bg-white border border-slate-200 p-5 rounded-md">
      <div className="overline mb-3">{title} · {items.length}</div>
      <div className="space-y-1 max-h-64 overflow-y-auto text-sm">
        {items.length === 0 ? <div className="text-slate-400 text-xs">Nenhum.</div> : items.map((s, i) => <div key={i} className="border-b border-slate-100 py-1 text-slate-700">{s}</div>)}
      </div>
    </div>
  );
}
